import { isClosedStatus } from '@bbbk/schema';
import type { CardState, PortalCard, PortalStay, PortalDog } from '@bbbk/schema';
import { PROOF_MAX_BYTES, PROOF_MIME_TYPES } from '@bbbk/schema';
import { monthInputToVaccineDue, parseVaccineDue, vaccineDueToMonthInput } from '../components/bookings/domain';
import { DOW_ABBR, MONTHS_ABBR, parseLocal } from '../lib/dates';
import type { TagTone } from './Tag';

// THE RULES BEHIND THE REGISTRATION SCREENS — pure, so every one of them is a
// unit test and the components only draw (customer-sign-in-registration).

/** `"MAR 27"` → `Mar 2027`; null for nothing or for text the parser cannot read. */
export function monthYearLabel(text: string | null | undefined): string | null {
  const d = parseVaccineDue(text);
  if (!d) return null;
  return `${MONTHS_ABBR[d.month - 1]} ${d.year}`;
}

/** The three states a date row draws (AC24): in date, needed, out of date. Month granularity. */
export type VaxMark = 'ok' | 'off' | 'bad';

export function vaxState(text: string | null | undefined, today: string): VaxMark {
  const d = parseVaccineDue(text);
  if (!d) return 'off';
  const [y, m] = today.split('-').map(Number);
  const nowIdx = y! * 12 + m!;
  return d.year * 12 + d.month < nowIdx ? 'bad' : 'ok';
}

export { monthInputToVaccineDue, vaccineDueToMonthInput };

// --- the month a person types on a phone (Freddi, 2026-09-14) ----------------
//
// `<input type="month">` was the wrong control here, and the research says so
// on three counts:
//
//   1. iOS renders it as a spinner wheel and draws the field BLANK when empty,
//      so it reads as decoration and gets skipped — on a required field that
//      means an error the customer did not know they were earning.
//   2. Support is patchy off iOS and Chrome; where it is missing the control
//      silently degrades to a plain text box with no hint of the format.
//   3. It takes `YYYY-MM`, which is not what is printed on the card in their
//      hand.
//
// What replaces it is the pattern Baymard measured for card expiry, which is
// the same job: ONE masked `MM/YY` field, typed as digits, the slash inserted
// for them. `type="text"` with `inputMode="numeric"` rather than
// `type="number"` — the number type strips leading zeros (so "03" becomes
// "3"), shows spinners, and changes value on scroll.

/** `"MAR 27"` → `"03/27"`, for the field. Empty for anything unreadable. */
export function vaccineDueToMask(text: string | null | undefined): string {
  const d = parseVaccineDue(text);
  if (!d) return '';
  return `${String(d.month).padStart(2, '0')}/${String(d.year).slice(-2)}`;
}

/** `"03/27"` → `"MAR 27"`, the shape the wire and the desk already use (AC42). */
export function maskToVaccineDue(mask: string): string {
  const m = /^(\d{2})\/(\d{2})$/.exec(mask.trim());
  if (!m) return '';
  const month = Number(m[1]);
  if (month < 1 || month > 12) return '';
  return `${MONTHS_ABBR[month - 1]!.toUpperCase()} ${m[2]}`;
}

/**
 * What the field should hold after this keystroke.
 *
 * Digits only, the slash placed for them, and a lone leading digit above 1 is
 * read as a month — typing "3" gives "03/" because there is no month 30-something.
 * Never longer than `MM/YY`.
 */
export function maskMonthYear(raw: string, prev?: string): string {
  // BACKSPACE HAS TO BE ABLE TO UNDO THE SLASH THIS FUNCTION ADDED (PR #228
  // review, codex).
  //
  // Typing `03` settles the field on `03/`. Backspace deletes the slash, this
  // runs again on `03`, sees two digits and puts the slash straight back — so
  // the month could not be erased from the end of the field at all, and on the
  // numeric keypad this input is built for there is no cursor to work around it
  // with.
  //
  // A pure mask cannot see a keystroke, but it can see what the field HELD: a
  // raw value that is exactly the previous one minus its trailing slash is the
  // slash being deleted, and what the customer meant to delete is the digit
  // before it. One more character comes off before the usual rules run.
  if (prev && prev.endsWith('/') && raw === prev.slice(0, -1)) {
    raw = raw.slice(0, -1);
  }
  // A PASTED `03/2027` IS A REAL THING PEOPLE DO. Stripping separators gave
  // `032027`, truncating to four took `0320`, and the field settled on
  // `03/20` — a wrong and usually expired medical date, submitted without a
  // word (P2.26, 2026-09-15).
  //
  // Six digits are ambiguous: `032027` is a pasted four-digit year, `032799`
  // is somebody overtyping a finished field. The century tells them apart —
  // only the first reads as `19xx`/`20xx` — so the paste keeps its last two
  // digits and the overtype truncates, as it always did.
  const all = raw.replace(/\D/g, '');
  const pastedYear = all.length === 6 && /^(19|20)/.test(all.slice(2));
  const digits = (pastedYear ? all.slice(0, 2) + all.slice(4) : all).slice(0, 4);
  if (digits === '') return '';
  if (digits.length === 1) return Number(digits) > 1 ? `0${digits}/` : digits;
  // AN IMPOSSIBLE MONTH IS NOT SILENTLY MADE POSSIBLE. `00` was clamped to
  // January and `13` to December, the mask then read as complete, and a
  // vaccination date nobody typed went onto an irreversible receipt (Codex,
  // 2026-09-15). The digits are kept as typed so the customer can see what they
  // put and fix it; `maskComplete` refuses them until they do.
  const mm = digits.slice(0, 2);
  return digits.length === 2 ? `${mm}/` : `${mm}/${digits.slice(2)}`;
}

/** Is the field finished and readable? A half-typed month is neither valid nor an error. */
export function maskComplete(mask: string): boolean {
  return maskToVaccineDue(mask) !== '';
}

/**
 * Has the customer typed something into this field that is NOT a date?
 *
 * Empty is fine — the dates are a claim and none is a valid claim (AC27). What
 * is not fine is `03/2` or `13/27` going up as nothing at all: the upload and
 * the irreversible registration both succeeded, the receipt reported the date
 * as still needed, and the value they could see on screen was never sent
 * (Codex, 2026-09-15).
 */
export function maskUnusable(mask: string): boolean {
  return mask.trim() !== '' && !maskComplete(mask);
}

/**
 * The receipt's tag on a dog, in the card's own words (AC32), in order of what
 * is missing: no card, no annual date, no kennel-cough date, a lapsed annual —
 * then the month in green.
 */
export function cardTag(dog: PortalDog, today: string): { tone: TagTone; text: string } {
  // WHAT WE ACTUALLY HOLD FOR THIS DOG, from either place it can live.
  //
  // This read `dog.card` alone (Freddi, 2026-09-17: "just to confirm this tag
  // should never be shown Annual date needed"). A card exists only where a
  // PHOTO was sent, so a customer who typed both dates and attached nothing was
  // told in red that we needed the date they had just given us — and so was any
  // dog whose date Patricia had typed at the desk. The bug predates the tag's
  // wording; it used to wear *Card needed*, which was wrong in the same way and
  // merely sounded less absurd.
  //
  // THE CARD'S CLAIM OUTRANKS THE COLUMN. Where both exist the photo is the
  // evidenced one. In practice they agree: registration writes what the
  // customer typed to the dog as well, and a card now makes both dates
  // mandatory, so nothing can send a claim without also filling the column.
  const annual = dog.card.validUntil ?? dog.vaccinesDue;
  const kc = dog.card.kcUntil ?? dog.kcDue;

  // NO DATE AT ALL ASKS FOR THE CARD (Freddi, 2026-09-17: "the tag should not
  // read Annual date needed. it should just say card needed in that case").
  //
  // Where we hold nothing for this dog, the card IS the ask. It is the source
  // every date comes from, and it is one thing to do rather than two fields to
  // find and copy off it — *Annual date needed* asked the customer for a fact
  // when what we want is the thing that carries it. `Annual date needed` is
  // retired and appears nowhere.
  //
  // WHAT CHANGED SINCE IT LED WITH THIS TAG is which question it answers. It
  // used to say *Card needed* whenever no PHOTO was on file, ignoring the dates
  // entirely — so a dog whose dates we held was told its card was missing. Now
  // the dates decide, and the card is named only when there is genuinely
  // nothing.
  if (!annual) return { tone: 'bad', text: 'Card needed' };

  // AN EXPIRY OUTRANKS A MISSING DATE. *Kennel cough needed* was returned
  // first, so a dog whose ANNUAL had already lapsed showed the smaller problem
  // and hid the bigger one (P2.30, 2026-09-15).
  //
  // THE DATE, AND THE COLOUR SAYS THE REST (Freddi, 2026-09-16: "no annual out
  // of date it should just say jan 2026, in red"). And THE DATE SHOWN IS THE
  // ONE THAT IS WRONG: it used to print the ANNUAL month whichever date had
  // lapsed, so a card valid to MAR 27 with a kennel cough gone in AUG 26 read
  // as a red "MAR 2027" and hid the problem (P2.29).
  if (vaxState(annual, today) === 'bad') {
    return { tone: 'bad', text: monthYearLabel(annual) ?? annual };
  }
  if (kc && vaxState(kc, today) === 'bad') {
    return { tone: 'bad', text: monthYearLabel(kc) ?? kc };
  }
  // RED, LIKE THE ANNUAL. The same fault — a date we need and do not have. It
  // was gold on the reasoning that *a card is on file and one line of it wants
  // filling in* is the smaller job; that distinction cannot arise any more,
  // because a card now makes BOTH dates mandatory before the step will submit.
  if (!kc) return { tone: 'bad', text: 'Kennel cough needed' };
  // BOTH DATES DECIDE THE TONE. Only the annual was checked, so a dog whose
  // KENNEL COUGH had lapsed still drew green and the customer was told they
  // were set (issue 7, 2026-09-15). The label stays the annual month — that is
  // the date the row is about — but an expired kennel cough is still expired.
  return { tone: 'ok', text: monthYearLabel(annual) ?? annual };
}

/** *Cocker Spaniel · on medication* — the line under a dog's name (AC17, AC32). */
export function dogLine(
  dog: Pick<PortalDog, 'breed' | 'meds'>,
  // THE CUSTOMER'S OWN ANSWER WINS where they have given one. A folded card
  // drew the desk's breed and medication straight off the API object, so the
  // moment they opened another dog their completed edits looked lost, right
  // before the submit (P2.27, 2026-09-15).
  answer?: { breed: string; meds: 'none' | 'yes'; instructions: string },
): string {
  const breed = (answer?.breed ?? dog.breed ?? '').trim() || 'Dog';
  const meds = answer ? answer.meds === 'yes' : Boolean(dog.meds);
  return meds ? `${breed} · on medication` : breed;
}

export function receiptLead(hasStay: boolean): string {
  return hasStay
    ? 'Here’s what we have. Anything wrong, message Patricia and she’ll fix it.'
    : 'Here’s what we have. Anything wrong, message Patricia.';
}

/** Everything after the first comma of the address — the town and county, nothing of the street (AC33). */
export function townCounty(address: string | null | undefined): string | null {
  if (!address) return null;
  // A NEWLINE SEPARATES A LINE OF AN ADDRESS TOO, and where there is no
  // separator at all the honest answer is nothing (Codex review, PR #228).
  // Returning the whole value as a fallback put "4 Church Street" into a
  // footer whose entire contract is town and county — the street is exactly
  // what AC33 keeps off this screen, so the fallback broke the one rule the
  // function exists to keep.
  const at = address.search(/[,\n]/);
  if (at === -1) return null;
  return address.slice(at + 1).replace(/\s*\n\s*/g, ', ').trim() || null;
}

/** *Bella’s ready for their first stay*; *Bracken and Paddy are ready for their first stay* (AC34). */
export function closingLine(dogs: Pick<PortalDog, 'name'>[]): string {
  const names = dogs.map((d) => d.name).filter(Boolean);
  if (names.length === 0) return 'Ready for a first stay';
  if (names.length === 1) return `${names[0]}’s ready for their first stay`;
  const last = names[names.length - 1];
  return `${names.slice(0, -1).join(', ')} and ${last} are ready for their first stay`;
}

/** The reason a file is refused before anything is sent (AC22), or null when it may go. */
export function fileProblem(file: Pick<File, 'size' | 'type'>): string | null {
  if (file.size > PROOF_MAX_BYTES) return `That file is too big — ${Math.round(PROOF_MAX_BYTES / (1024 * 1024))} MB is the limit.`;
  const mime = file.type.split(';')[0]!.trim().toLowerCase();
  if (!(PROOF_MIME_TYPES as readonly string[]).includes(mime)) return 'That file type isn’t accepted — send a JPEG, PNG, HEIC or PDF.';
  return null;
}

/** `2201000` → `2.1 MB`; under a megabyte, `640 KB`. */
export function fileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** The next upcoming stay, and whether any stay is already behind them (AC35's first/next). */
export function nextStay(stays: PortalStay[], today: string): { stay: PortalStay | null; hadOne: boolean } {
  const live = stays.filter((s) => !isClosedStatus(s.status));
  const upcoming = live.filter((s) => s.checkOut >= today).sort((a, b) => a.checkIn.localeCompare(b.checkIn));
  const hadOne = live.some((s) => s.checkOut < today);
  return { stay: upcoming[0] ?? null, hadOne };
}

/** `2026-10-17` → `Sat 17 Oct`. */
export function stayDay(iso: string): string {
  const d = parseLocal(iso);
  return `${DOW_ABBR[d.getDay()]} ${d.getDate()} ${MONTHS_ABBR[d.getMonth()]}`;
}

export function halfOfDay(half: string | undefined): string {
  if (half === 'am') return 'morning';
  if (half === 'pm') return 'afternoon';
  return '';
}

export function nights(checkIn: string, checkOut: string): number {
  return Math.max(0, Math.round((parseLocal(checkOut).getTime() - parseLocal(checkIn).getTime()) / 86_400_000));
}

/** The dogs on a stay, matched back to the household's dogs by name — the timeline carries names only. */
export function dogsOnStay(stay: PortalStay, dogs: PortalDog[]): PortalDog[] {
  // IDS FIRST. Matching on the joined NAMES put both of a household's two dogs
  // called Max onto a stay only one of them was booked for, and a name with a
  // comma in it split into two that matched nothing (P2.31, 2026-09-15). The
  // names are still the fallback for a caller that predates `dogIds`.
  if (stay.dogIds.length > 0) {
    const ids = new Set(stay.dogIds);
    return dogs.filter((d) => ids.has(d.id));
  }
  const names = stay.dogs.split(',').map((n) => n.trim().toLowerCase()).filter(Boolean);
  // NO MATCH MEANS NO MATCH (Codex review, PR #228). Falling back to the whole
  // household stated, on the receipt, that every dog was on a stay none of them
  // may be on — the live case is a stay whose only dog has since been archived,
  // which the timeline still names and `/portal/me` no longer returns. An empty
  // list is a card the caller can draw honestly; a wrong list is not.
  return dogs.filter((d) => names.includes(d.name.toLowerCase()));
}

/** *Bracken & Paddy* and *Springer Spaniel · Cocker Spaniel* — the stay card's head. */
export function stayHead(dogs: PortalDog[]): { names: string; breeds: string } {
  return {
    names: dogs.map((d) => d.name).join(' & '),
    breeds: dogs.map((d) => d.breed?.trim() || 'Dog').join(' · '),
  };
}

/**
 * What the dogs step's one button must do when it is pressed (AC28, AC29).
 *
 * `write` — the normal path: send the cards, send the registration, then read
 * the receipt back.
 * `reread` — registration has ALREADY landed and only the read-back failed.
 *
 * The distinction is the whole fix. `registered_at` is stamped once (AC29), so
 * re-sending answers 409 — and a customer whose post-write reload failed was
 * left on a screen whose only button could no longer work, with the once-only
 * receipt gone for good (Codex, PR #228). After the write lands, nothing may
 * send it again; the retry retries the READ, which is the only thing that
 * actually failed.
 */
export function dogsStepAction(registrationLanded: boolean): 'write' | 'reread' {
  return registrationLanded ? 'reread' : 'write';
}

/**
 * WHY THE DOGS STEP CANNOT BE SUBMITTED YET, or null when it can.
 *
 * REGISTRATION IS ONCE-ONLY (AC29). Everything the customer gets wrong here
 * they get wrong forever — the portal gives them no second go — so a value
 * that cannot be sent must stop the submit rather than be quietly dropped on
 * the way. Three ways that was happening (2026-09-15):
 *
 *   * A cleared dog name fell back to the desk's old spelling in the payload,
 *     so a customer fixing a misspelling and submitting before retyping had
 *     their correction thrown away with nothing on screen to say so.
 *   * *Medication: Yes* with empty Instructions sent `null`, which is exactly
 *     what *None* sends — so the receipt and the desk both then said the dog
 *     was on no medication.
 *   * A half-typed or impossible date (`03/2`, `13/27`) went up as no date at
 *     all, and the receipt reported it as still needed.
 *
 * Returns the id of the dog at fault, so the step can point at it.
 */
export type BlockedField = 'name' | 'instructions' | 'validUntil' | 'kcUntil' | 'file';

/**
 * WHY a date stopped the submit. Two different faults want two different
 * things said: `unusable` is `03/2` sitting in the field where the customer can
 * see it, `required` is an empty field that a card has just made mandatory.
 */
export type BlockedWhy = 'unusable' | 'required';

export interface DogsBlock {
  dogId: string;
  field: BlockedField;
  why?: BlockedWhy;
}

export function dogsStepBlocker(
  dogs: { id: string; card?: { state: CardState } }[],
  answers:
    Record<string, {
      name: string;
      meds: 'none' | 'yes';
      instructions: string;
      validUntil: string;
      kcUntil: string;
      fileError?: string | null;
      file?: File | null;
    } | undefined>,
): DogsBlock | null {
  for (const dog of dogs) {
    const a = answers[dog.id];
    if (!a) continue;
    // A REFUSED FILE IS NOT NO FILE. It was set to `file: null` with an error
    // beside it, and the submit then skipped every null file and stamped
    // registration anyway — so the screen said the card was rejected and the
    // customer lost the chance to send it, permanently (P1.13, 2026-09-15).
    if (a.fileError) return { dogId: dog.id, field: 'file' };
    if (a.name.trim() === '') return { dogId: dog.id, field: 'name' };
    if (a.meds === 'yes' && a.instructions.trim() === '') return { dogId: dog.id, field: 'instructions' };
    if (maskUnusable(a.validUntil)) return { dogId: dog.id, field: 'validUntil', why: 'unusable' };
    if (maskUnusable(a.kcUntil)) return { dogId: dog.id, field: 'kcUntil', why: 'unusable' };

    // A CARD MAKES BOTH DATES MANDATORY (Freddi, 2026-09-17: "we need to make
    // both vaccine fields mandatory if a user uploads a screenshot, if no
    // screenshot they dont need to").
    //
    // The card is evidence and the dates are the claim it evidences. A card
    // with no dates beside it leaves the desk holding a photograph somebody has
    // to squint at and type up — which is the double entry this whole flow
    // exists to end. Without a card there is nothing to back up and nothing to
    // contradict, so a blank pair stays a finished answer: not every household
    // has a card to photograph, and we take the dates on their own.
    //
    // A CARD ALREADY ON FILE COUNTS. The resume case — sent in an earlier
    // sitting, come back to finish — is the same situation from the desk's side,
    // and registration is once-only, so this is the last chance to ask.
    if (!hasCard(dog, a)) continue;
    if (!maskComplete(a.validUntil)) return { dogId: dog.id, field: 'validUntil', why: 'required' };
    if (!maskComplete(a.kcUntil)) return { dogId: dog.id, field: 'kcUntil', why: 'required' };
  }
  return null;
}

/**
 * What the two date fields should hold once a NEW photo is chosen.
 *
 * CLEAR THE CARD'S CLAIM, KEEP EVERYTHING ELSE (Freddi, 2026-09-19: "if
 * patricia has seeded anything that should appear in the registration form so
 * the vaccination dates patricia had filled in on her end should be pre filled
 * on the reg form").
 *
 * The clear exists for a real reason (P2.24, 2026-09-15): where a card is
 * already on file the dates were seeded FROM it, and a replacement is sent
 * precisely because the two differ — so leaving them meant the new photo went
 * up carrying the old card's claims. That reasoning covers exactly one kind of
 * value, and the code cleared all of them.
 *
 * Patricia's own typing is not a stale card claim. It is the best thing we hold
 * about the dog, and blanking it asked the customer to retype what we already
 * had — or, if they then removed the photo, lost it for good with nothing on
 * screen to say so. The customer's own typing is theirs and is not ours to bin
 * either.
 *
 * So the test is per field and it is narrow: the value goes only if it IS the
 * card's claim.
 */
export function datesOnNewCard(
  dog: { card: PortalCard },
  answer: { validUntil: string; kcUntil: string },
): { validUntil: string; kcUntil: string } {
  const drop = (shown: string, claim: string | null): string => {
    if (dog.card.state !== 'added' || !claim) return shown;
    return shown === vaccineDueToMask(claim) ? '' : shown;
  };
  return {
    validUntil: drop(answer.validUntil, dog.card.validUntil),
    kcUntil: drop(answer.kcUntil, dog.card.kcUntil),
  };
}

/** Is there a card in play for this dog — chosen on this phone, or already sent? */
export function hasCard(
  dog: { card?: { state: CardState } },
  answer: { file?: File | null },
): boolean {
  return (answer.file ?? null) !== null || dog.card?.state === 'added';
}
